import Layout from '../components/Layout';
import { STEPS, INCLUDED, TRUST_BADGES } from '../mock';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ShieldCheck, QrCode, LifeBuoy, Wallet, Wrench, Play } from 'lucide-react';
import TagVisual from '../components/TagVisual';
import { useState } from 'react';

const iconMap = { ShieldCheck, QrCode, LifeBuoy, Wallet, Wrench };

export default function HowItWorksPage() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <Layout>
      <section className="pt-16 pb-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="eyebrow mb-6">How it works</p>
          <h1 className="h-display text-5xl md:text-6xl lg:text-7xl">Someone scans. You stay <em className="font-serif-display italic font-normal text-navy">private</em>. That&apos;s it.</h1>
          <p className="mt-6 text-lg text-neutral-600 max-w-2xl mx-auto">No app to install for the person reaching you, and no number ever revealed for you.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 space-y-4">
          {STEPS.map((s, i) => (
            <div key={s.num} className={`flex gap-5 md:gap-8 items-start rounded-3xl p-6 md:p-8 border transition-all hover:shadow-lg reveal ${i === STEPS.length - 1 ? 'bg-navy text-white border-transparent' : 'bg-white border-black/5'}`} style={{ animationDelay: `${i * 0.08}s` }}>
              <div className={`rounded-2xl w-16 h-16 md:w-20 md:h-20 flex items-center justify-center flex-shrink-0 ${i === STEPS.length - 1 ? 'bg-white text-navy' : 'bg-navy text-white'}`}>
                <span className="h-display text-2xl md:text-3xl">{s.num}</span>
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-semibold tracking-tight">{s.title}</h3>
                <p className={`mt-2 leading-relaxed ${i === STEPS.length - 1 ? 'text-blue-100' : 'text-neutral-600'}`}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="eyebrow mb-4">On your vehicle</p>
            <h2 className="h-display text-4xl md:text-5xl">Stick it once. <em className="font-serif-display italic font-normal text-navy">Stay reachable</em> forever.</h2>
            <p className="mt-5 text-neutral-600 text-lg max-w-md">Peel, place on your windshield or rear glass, and activate in under a minute. Waterproof and built for Indian weather.</p>
            <div className="mt-8 flex gap-3">
              <Link to="/shop" className="btn-primary">Get your tag <ArrowRight size={16} /></Link>
              <Link to="/scan/demo" className="btn-outline">Try demo scan</Link>
            </div>
          </div>
          <div className="flex justify-center">
            <TagVisual size={340} />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="h-display text-4xl md:text-5xl">Trust built into every <em className="font-serif-display italic font-normal text-navy">tag</em></h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TRUST_BADGES.slice(0, 3).map((f) => {
              const Ic = iconMap[f.icon] || ShieldCheck;
              return (
                <div key={f.title} className="card-soft p-7 hover:-translate-y-1 transition-transform">
                  <div className="w-12 h-12 rounded-xl bg-navy text-white flex items-center justify-center mb-5"><Ic size={20} /></div>
                  <h3 className="text-lg font-semibold">{f.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="eyebrow mb-4">Included free</p>
          <h2 className="h-display text-4xl md:text-5xl">Everything included, for <em className="font-serif-display italic font-normal text-navy">life</em></h2>
          <div className="card-soft mt-10 p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 text-left">
            {INCLUDED.map((it) => (
              <div key={it} className="flex items-center gap-3 border-b border-black/5 pb-3 last:border-0">
                <Check size={16} className="text-navy" /><span className="font-medium">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center text-white">
          <h2 className="h-display text-4xl md:text-5xl">Ready to stay reachable, <em className="font-serif-display italic font-normal text-blue-200">safely?</em></h2>
          <p className="mt-5 text-blue-100">Get your tag today — free delivery and COD available.</p>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-navy py-3 px-6 font-semibold hover:bg-blue-50">Buy the tag <ArrowRight size={16} /></Link>
        </div>
      </section>
    </Layout>
  );
}
